import { useState } from 'react';
import type { Note, NoteKind } from '../types';
import { postNote } from '../lib/api';
import Avatar from './Avatar';

const KIND_LABEL: Record<NoteKind, string> = {
  question: 'Asked', answer: 'Answered', note: 'Note',
};
const KIND_TONE: Record<NoteKind, string> = {
  question: 'p-warn', answer: 'p-ok', note: 'p-mute',
};

/** Short enough to sit beside a name without wrapping the row. */
function when(iso: string) {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  if (mins < 60 * 24 * 7) return `${Math.round(mins / (60 * 24))}d ago`;
  return new Date(iso).toLocaleDateString();
}

export interface NoteThreadProps {
  notes: Note[];
  /** Where a new note lands. For a deck prompt this is the question half, so
   *  one card keeps one thread however many fields it is stored in. */
  unitId: string;
  locale: string;
  meId: string;
  onPosted: () => void;
  /** Owners get a queue item closed by answering; reviewers get plain replies. */
  placeholder?: string;
}

export default function NoteThread({
  notes, unitId, locale, meId, onPosted, placeholder,
}: NoteThreadProps) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Answering is for settling somebody else's open question. Replying to your
  // own, or adding context where nothing is open, is just a note — it must not
  // silently mark the question resolved.
  const openByOther = notes.some(
    (n) => n.kind === 'question' && !n.answered_at && n.author_id !== meId);
  const kind: NoteKind = openByOther ? 'answer' : 'note';

  async function post() {
    const text = body.trim();
    if (!text) return;
    setBusy(true); setError(null);
    try {
      await postNote(unitId, locale, text, kind);
      setBody('');
      onPosted();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <div className="thread">
      {notes.map((n) => (
        <div className={`tnote${n.kind === 'question' && !n.answered_at ? ' open' : ''}`} key={n.id}>
          <Avatar name={n.author_name} url={n.author_avatar} size={22} />
          <div className="tb">
            <div className="th">
              <b>{n.author_name}</b>
              {n.author_is_owner && <span className="pill p-acc">owner</span>}
              <span className={`pill ${KIND_TONE[n.kind]}`}>{KIND_LABEL[n.kind]}</span>
              <span className="tw">{when(n.created_at)}</span>
            </div>
            <p className="tt">{n.body}</p>
          </div>
        </div>
      ))}

      {!notes.length && (
        <p className="tempty">
          Nothing has been asked about this one yet. Leave a note if something here needs
          explaining — whoever reads this prompt next will see it.
        </p>
      )}

      <div className="treply">
        <textarea
          className="note" value={body} disabled={busy}
          onChange={(e) => setBody(e.target.value)}
          placeholder={placeholder ?? (openByOther
            ? 'Answer this — everyone reviewing this language will see it here.'
            : 'Add a note for whoever reads this prompt next.')}
        />
        <div className="acts">
          <button className="btn sm pri" disabled={!body.trim() || busy} onClick={() => void post()}>
            {busy ? 'Posting…' : openByOther ? 'Post answer' : 'Post note'}
          </button>
          <span className="editcue">
            {openByOther
              ? 'Posting an answer marks the question settled and clears it from the queue.'
              : 'Posted straight away — this one does not go through your batch.'}
          </span>
        </div>
        {error && <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>}
      </div>
    </div>
  );
}
